// Feature-flag-protected wrapper for InteractiveBoothMap (Issue #676, Issue #1078)
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import InteractiveBoothMap from './InteractiveBoothMap';
import { fairApi } from '../services/api';
import { BoothLayout, BoothAmenity, BoothMapSlotApi, BoothMapSlot } from '../types';
import { extractApiError } from '../utils/errorUtils';

interface InteractiveBoothMapWrapperProps {
  fairId?: number;
  layouts?: BoothLayout[];
  amenities?: BoothAmenity[];
  slots?: BoothMapSlot[];
  onBoothClick?: (slotId: number) => void;
  highlightedSlotId?: number;
}

/**
 * Feature-flag-protected wrapper for InteractiveBoothMap component.
 *
 * Checks the 'booth_map_enabled' feature flag before rendering the map.
 * If disabled, shows a simple message. If loading, shows a loading state.
 *
 * Issue #676: Interactive Booth Map with Visual Layout
 * Issue #1078: Fetch booth map data from API and integrate with routing
 */
const InteractiveBoothMapWrapper: React.FC<InteractiveBoothMapWrapperProps> = (props) => {
  const { fairId: routeFairId } = useParams<{ fairId: string }>();
  // Issue #1078: Guard against NaN from invalid route params
  const parsedRouteFairId = routeFairId ? Number(routeFairId) : undefined;
  const fairId = props.fairId || (parsedRouteFairId && !isNaN(parsedRouteFairId) ? parsedRouteFairId : undefined);

  // Issue #1078: Use correct feature flag 'booth_map_enabled'
  const { isEnabled, isLoading: isFlagLoading, error: flagError } = useFeatureFlag('booth_map_enabled');
  const [boothMapData, setBoothMapData] = useState<{
    layouts: BoothLayout[];
    amenities: BoothAmenity[];
    slots: BoothMapSlot[];
  } | null>(null);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [dataError, setDataError] = useState<string | null>(null);

  // Fetch booth map data from API when fairId is available
  // Issue #1078: Skip fetch if props data is already provided
  useEffect(() => {
    // Skip fetch if props provide all required data
    if (props.layouts && props.amenities && props.slots) {
      return;
    }

    if (!fairId || !isEnabled) return;

    // Issue #1078: Use AbortController to cancel fetch on unmount
    const abortController = new AbortController();

    const fetchBoothMapData = async () => {
      try {
        setIsLoadingData(true);
        setDataError(null);
        const data = await fairApi.getBoothMap(fairId);

        // Check if component unmounted during fetch
        if (abortController.signal.aborted) return;

        // Issue #1078: Type-safe mapping from API slot format to map component format
        // API uses: is_corner_booth, is_high_traffic, is_near_entrance
        // Map expects: corner_booth, high_traffic_area, near_entrance
        const mappedSlots: BoothMapSlot[] = data.slots.map((apiSlot: BoothMapSlotApi) => ({
          ...apiSlot,
          corner_booth: apiSlot.is_corner_booth || false,
          high_traffic_area: apiSlot.is_high_traffic || false,
          near_entrance: apiSlot.is_near_entrance || false,
        }));

        setBoothMapData({
          layouts: data.layouts,
          amenities: data.amenities,
          slots: mappedSlots,
        });
      } catch (err: unknown) {
        // Don't update state if component unmounted
        if (abortController.signal.aborted) return;

        const apiError = extractApiError(err);
        console.error('Failed to load booth map data:', apiError);
        setDataError(apiError.message || 'Failed to load booth map data');
      } finally {
        // Don't update state if component unmounted
        if (!abortController.signal.aborted) {
          setIsLoadingData(false);
        }
      }
    };

    fetchBoothMapData();

    // Cleanup: abort fetch on unmount
    return () => {
      abortController.abort();
    };
  }, [fairId, isEnabled, props.layouts, props.amenities, props.slots]);

  const isLoading = isFlagLoading || isLoadingData;

  if (isLoading) {
    return (
      <div className="interactive-booth-map-loading p-8 text-center bg-gray-50 rounded-lg border border-gray-200" data-testid="booth-layout-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-gray-600">Loading booth map...</p>
      </div>
    );
  }

  // Issue #1078: Show error state if feature flag check failed (don't mask operational failures)
  if (flagError) {
    console.error('Feature flag check failed for booth_map_enabled:', flagError);
    return (
      <div className="interactive-booth-map-error p-8 text-center bg-red-50 rounded-lg border border-red-200">
        <svg
          className="mx-auto h-16 w-16 text-red-400 mb-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Feature Configuration Error</h3>
        <p className="text-gray-600 mb-4">
          Unable to check if the booth map feature is enabled.
        </p>
        <p className="text-sm text-gray-500">
          Please try refreshing the page or contact support if the problem persists.
        </p>
      </div>
    );
  }

  // Show disabled state if feature flag is not enabled
  if (!isEnabled) {
    return (
      <div className="interactive-booth-map-disabled p-8 text-center bg-gray-50 rounded-lg border border-gray-200">
        <svg
          className="mx-auto h-16 w-16 text-gray-400 mb-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
          />
        </svg>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Interactive Booth Map Unavailable</h3>
        <p className="text-gray-600 mb-4">
          The interactive booth map feature is currently disabled.
        </p>
        <p className="text-sm text-gray-500">
          Please contact an administrator for more information.
        </p>
      </div>
    );
  }

  // Issue #1078: Only show error if fetch failed AND no props data exists as fallback
  // If props provide data, use that even if API fetch failed
  const hasPropsData = props.layouts || props.amenities || props.slots;
  if (dataError && !hasPropsData) {
    return (
      <div className="interactive-booth-map-error p-8 text-center bg-red-50 rounded-lg border border-red-200">
        <svg
          className="mx-auto h-16 w-16 text-red-400 mb-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to Load Booth Map</h3>
        <p className="text-gray-600 mb-4">{dataError}</p>
        <p className="text-sm text-gray-500">
          Please try refreshing the page or contact support if the problem persists.
        </p>
      </div>
    );
  }

  // Issue #1078: Guard against missing fairId/data before rendering
  // Ensure we have either props data OR fetched data before rendering the map
  const layouts = props.layouts || boothMapData?.layouts || [];
  const amenities = props.amenities || boothMapData?.amenities || [];
  const slots = props.slots || boothMapData?.slots || [];

  // Show empty state if no data is available and no fairId to fetch
  if (!fairId && !props.layouts && !props.amenities && !props.slots) {
    return (
      <div className="interactive-booth-map-empty p-8 text-center bg-gray-50 rounded-lg border border-gray-200">
        <svg
          className="mx-auto h-16 w-16 text-gray-400 mb-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
          />
        </svg>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Booth Map Data</h3>
        <p className="text-gray-600 mb-4">
          No booth map data is available for this fair.
        </p>
        <p className="text-sm text-gray-500">
          Please check back later or contact the fair organizer.
        </p>
      </div>
    );
  }

  // Issue #1078: Show empty state if API returned empty arrays (successful fetch but no data)
  if (fairId && boothMapData && layouts.length === 0 && slots.length === 0) {
    return (
      <div className="interactive-booth-map-empty p-8 text-center bg-gray-50 rounded-lg border border-gray-200">
        <svg
          className="mx-auto h-16 w-16 text-gray-400 mb-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
          />
        </svg>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Booth Layouts Available</h3>
        <p className="text-gray-600 mb-4">
          This fair does not have any booth layouts configured yet.
        </p>
        <p className="text-sm text-gray-500">
          The fair organizer has not set up the booth map. Please check back later.
        </p>
      </div>
    );
  }

  // Feature flag is enabled - render the actual component with data
  return (
    <div data-testid="interactive-booth-map-wrapper">
      <InteractiveBoothMap
        fairId={fairId}
        layouts={layouts}
        amenities={amenities}
        slots={slots}
        onBoothClick={props.onBoothClick}
        highlightedSlotId={props.highlightedSlotId}
      />
    </div>
  );
};

export default InteractiveBoothMapWrapper;
